import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';

import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import { useDebounce } from '../../shared/hooks/use-debounce.hook';
import { MIN_SEARCH_LENGTH } from '../../shared/constants/search.constants';
import { ADMIN_DELIVERY_POINTS_API_PATHS } from '../../delivery-points/constants/delivery-points-api-paths.constants';
import { DeliveryPoint, GetDeliveryPointsResponse } from '../../delivery-points/types/delivery-point.type';

type UseSearchDeliveryPointsResult = {
  isSearching: boolean;
  deliveryPoints: DeliveryPoint[];
};

const SEARCH_DEBOUNCE_DELAY_MS = 400;

const SEARCH_RESULTS_LIMIT = 25;

const INITIAL_STATE: UseSearchDeliveryPointsResult = {
  isSearching: false,
  deliveryPoints: [],
};

export function useSearchDeliveryPoints({ query }: { query: string }): UseSearchDeliveryPointsResult {
  const { enqueueSnackbar } = useSnackbar();
  const [result, setResult] = useState<UseSearchDeliveryPointsResult>(INITIAL_STATE);
  const debouncedQuery = useDebounce(query, SEARCH_DEBOUNCE_DELAY_MS).trim();
  const isSearchable = debouncedQuery.length >= MIN_SEARCH_LENGTH;

  useEffect(() => {
    if (!isSearchable) return undefined;

    let isCurrent = true;
    setResult((previous) => ({ ...previous, isSearching: true }));

    HttpClient.get<GetDeliveryPointsResponse>(ADMIN_DELIVERY_POINTS_API_PATHS.GET_DELIVERY_POINTS, {
      params: {
        page: 1,
        limit: SEARCH_RESULTS_LIMIT,
        sortBy: 'name',
        sortDirection: 'asc',
        filters: JSON.stringify({ name: debouncedQuery, isActive: true }),
      },
    })
      .then((response) => {
        if (!isCurrent) return;
        setResult({ isSearching: false, deliveryPoints: response.deliveryPoints });
      })
      .catch((error) => {
        if (!isCurrent) return;
        enqueueSnackbar(
          extractTranslatedApiErrorMessage(
            error,
            'Une erreur est survenue lors de la recherche des points de livraison'
          ),
          { variant: 'error' }
        );
        setResult(INITIAL_STATE);
      });

    return () => {
      isCurrent = false;
    };
  }, [debouncedQuery, isSearchable, enqueueSnackbar]);

  if (!isSearchable) return INITIAL_STATE;

  return result;
}
